import type { UseFormReturn } from "react-hook-form";

import type { User } from "@gsa/auth/types";
import type { CustomerSchema } from "@gsa/db/schema";
import { Button } from "@gsa/ui/components/ui/button";
import { Card, CardContent, CardHeader } from "@gsa/ui/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@gsa/ui/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@gsa/ui/components/ui/form";
import { Input } from "@gsa/ui/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@gsa/ui/components/ui/popover";
import { CheckIcon, ChevronsUpDownIcon } from "@gsa/ui/icons";
import { cn } from "@gsa/ui/lib/utils";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gsa/ui/components/ui/select";
import { H2 } from "~/app/ui/typography";
import countries from "~/data/countries-min.json";
import states from "~/data/states-min.json";
import type { CreateGuestOrderSchema } from "~/lib/types";

interface Props {
  customer:
    | Omit<
        CustomerSchema,
        | "createdAt"
        | "updatedAt"
        | "note"
        | "source"
        | "authorizeNetCustomerId"
        | "dealer"
        | "dealerName"
      >
    | null
    | undefined;
  user: User | null | undefined;
  form: UseFormReturn<CreateGuestOrderSchema>;
}

export function ContactInformationForm({ form }: Props) {
  const selectedBillingCountry = countries.find(
    (c) => c.iso2 === form.watch("customer.billingCountry")
  );
  const selectedBillingCountryStates =
    states.find((s) => s.id === selectedBillingCountry?.id)?.states ?? [];

  const selectedBillingState = selectedBillingCountryStates.find(
    (s) => s.state_code === form.watch("customer.billingState")
  );

  const selectedShippingCountry = countries.find(
    (c) => c.iso2 === form.watch("customer.shippingCountry")
  );
  const selectedShippingCountryStates =
    states.find((s) => s.id === selectedShippingCountry?.id)?.states ?? [];
  const selectedShippingState = selectedShippingCountryStates.find(
    (s) => s.state_code === form.watch("customer.shippingState")
  );

  return (
    <>
      <Card>
        <CardHeader>
          <H2>Customer Details</H2>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid gap-4">
            <FormField
              control={form.control}
              name="customer.customerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      autoFocus
                      autoCapitalize="sentences"
                      required
                      name="customerName"
                      id="customerName"
                      autoComplete="name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customer.customerEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email Address <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      required
                      autoComplete="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customer.customerPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="tel"
                      autoCapitalize="off"
                      autoCorrect="off"
                      autoComplete="tel"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <H2>Billing Address</H2>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid gap-4">
            <div className=" grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
              <FormField
                control={form.control}
                name="customer.billingAddress1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Address <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value}
                        type="text"
                        required
                        autoComplete="street-address billing"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.billingAddress2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apartment, suite, etc.</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        autoComplete="address-line2 billing"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.billingCountry"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>
                      Country <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        {...field}
                        value={field.value}
                        onValueChange={(value) => {
                          form.setValue("customer.billingCountry", value);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.iso2} value={country.iso2}>
                              {country.emoji} {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.billingCity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      City <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        required
                        autoComplete="address-level2 billing"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.billingState"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      State / Province <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              disabled={!form.watch("customer.billingCountry")}
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {selectedBillingState?.name ?? "Select state"}
                              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="max-h-[30vh] w-[260px] overflow-y-scroll p-0">
                          <Command>
                            <CommandInput placeholder="Search states..." />
                            <CommandEmpty>No states found.</CommandEmpty>
                            <CommandGroup>
                              {selectedBillingCountryStates?.map((state) => (
                                <CommandItem
                                  value={state.name}
                                  key={state.name}
                                  onSelect={() => {
                                    form.setValue(
                                      "customer.billingState",
                                      state.state_code
                                    );
                                  }}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      state.state_code === field.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {state.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.billingZip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Postal code <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        required
                        autoComplete="postal-code billing"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.billingCompany"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        autoComplete="company billing"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <H2>Shipping Address</H2>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid gap-4">
            <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
              <FormField
                control={form.control}
                name="customer.shippingAddress1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Address <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value}
                        type="text"
                        required
                        autoComplete="street-address shipping"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.shippingAddress2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apartment, suite, etc.</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        autoComplete="address-line2 shipping"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.shippingCountry"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>
                      Country <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        {...field}
                        value={field.value}
                        onValueChange={(value) => {
                          form.setValue("customer.shippingCountry", value);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.iso2} value={country.iso2}>
                              {country.emoji} {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.shippingCity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      City <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        required
                        autoComplete="address-level2 shipping"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.shippingState"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      State / Province <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              disabled={!form.watch("customer.shippingCountry")}
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {selectedShippingState?.name ?? "Select state"}
                              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="max-h-[30vh] w-[280px] overflow-y-scroll p-0">
                          <Command>
                            <CommandInput placeholder="Search states..." />
                            <CommandEmpty>No states found.</CommandEmpty>
                            <CommandGroup>
                              {selectedShippingCountryStates?.map((state) => (
                                <CommandItem
                                  value={state.name}
                                  key={state.name}
                                  onSelect={() => {
                                    form.setValue(
                                      "customer.shippingState",
                                      state.state_code
                                    );
                                  }}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      state.state_code === field.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {state.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.shippingZip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Postal code <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        required
                        autoComplete="postal-code shipping"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer.shippingCompany"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        autoComplete="company shipping"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
